import React, { Dispatch, SetStateAction, useContext } from 'react';
import { Icon, Typography } from '@/components/common';
import Icons from '@/const/icons';
import { UserToPayItem } from '@/interfaces';
import { formatCurrencyAlt } from '@/utils';
import {
	FieldValues,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';
import { InputText } from '@/components/form';
import { CaseContext } from '@/context/CaseContext';

interface Props {
	usersList: UserToPayItem[];
	isDisabled?: boolean;
	setError: Dispatch<SetStateAction<{ message: string } | null>>;
	setUsersList: Dispatch<SetStateAction<UserToPayItem[]>>;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	getValues: UseFormGetValues<FieldValues>;
}

export const PaymentGroup: React.FC<Props> = ({
	usersList,
	setError,
	setUsersList,
	register,
	setValue,
	getValues,
	isDisabled = false,
}) => {
	const { isCaseReadOnly } = useContext(CaseContext);
	const disabled = isCaseReadOnly || isDisabled;

	const handleBlur = (name: string) => {
		setValue(name, formatCurrencyAlt(getValues(name), 'blur'));
		const userListAmounts = usersList.map((user) => {
			user.formattedAmount = formatCurrencyAlt(
				user?.formattedAmount ?? '',
				'blur'
			);

			return user;
		});
		setUsersList(userListAmounts);
	};

	const handleChange = (event: any, name: string) => {
		const rawValue = event.target.value;
		const formatted = formatCurrencyAlt(rawValue, handleBlur);
		const amount = rawValue ? Number(rawValue.replace(/[^0-9.]/g, '')) : null;

		setValue(name, formatted);

		const userListAmounts = usersList.map((user) => {
			user.amount = amount;
			user.formattedAmount = formatted;
			user.error = null;

			return user;
		});

		const count = userListAmounts.reduce((acc, item) => {
			return !item.amount || item.amount <= 0 ? acc + 1 : acc;
		}, 0);

		if (count === 0) {
			setError(null);
		}
		setUsersList(userListAmounts);
	};

	return (
		<div className="space-y-2 w-[28.43rem]">
			<div className="w-[336px]">
				<InputText
					disabled={disabled}
					register={register}
					name="amount"
					title="Enter Individual Settlement Amounts"
					className="w-full"
					iconLeft={disabled ? Icons.currency : Icons.currencyBlack}
					onChange={(e) => handleChange(e, 'amount')}
					onBlur={() => handleBlur('amount')}
					classNameIcon="!h-4 !w-4 mt-1 !text-primary"
					classNameInput="!pl-9"
				/>
			</div>
			<div className="flex items-start gap-x-2 w-full">
				<Icon
					src={Icons.infoCircle}
					width={24}
					height={24}
					className="text-main-orange"
				/>
				<Typography type="regular-12" className="text-opacity-black">
					The amount you entered is the amount that will be paid to each
					individual customer. Make sure it is the correct amount.
				</Typography>
			</div>
		</div>
	);
};

// import React, { Dispatch, SetStateAction } from 'react';
// import clsx from 'clsx';
// import { HeaderFilterTable, Icon, Typography } from '@/components/common';
// import Icons from '@/const/icons';
// import { AvatarName } from '@/components/dashboard';
// import { UserToPayItem } from '@/interfaces';
// import { formatCurrencyAlt } from '@/utils';

// interface Props {
// 	usersList: UserToPayItem[];
// 	setError: Dispatch<SetStateAction<{ message: string } | null>>;
// 	setUsersList: Dispatch<SetStateAction<UserToPayItem[]>>;
// }

// export const PayIndividually: React.FC<Props> = ({
// 	usersList,
// 	setError,
// 	setUsersList,
// }) => {
// 	const [filter, setFilter] = React.useState<string | undefined>();
// 	const optionsFilter = [
// 		{
// 			label: 'Payment Entered',
// 			value: 'payment-entered',
// 			icon: (
// 				<Icon
// 					src={Icons.check}
// 					width={18}
// 					height={18}
// 					className="text-green-120"
// 				/>
// 			),
// 		},
// 		{
// 			label: 'Payment Not Entered',
// 			value: 'payment-not-entered',
// 			icon: (
// 				<Icon
// 					src={Icons.close}
// 					width={18}
// 					height={18}
// 					className="text-main-red"
// 				/>
// 			),
// 		},
// 	];

// 	const handleBlur = (applicationId?: number) => {
// 		const userListAmounts = usersList.map((user) => {
// 			if (applicationId === user.application_id) {
// 				user.formattedAmount = formatCurrencyAlt(
// 					user?.formattedAmount ?? '',
// 					'blur'
// 				);
// 			}
// 			return user;
// 		});
// 		setUsersList(userListAmounts);
// 	};

// 	const handleChange = (event: any, applicationId?: number) => {
// 		const rawValue = event.target.value;
// 		const formatted = formatCurrencyAlt(rawValue, handleBlur);
// 		const amount = rawValue ? Number(rawValue.replace(/[^0-9.]/g, '')) : null;

// 		const userListAmounts = usersList.map((user) => {
// 			if (applicationId === user.application_id) {
// 				user.amount = amount;
// 				user.formattedAmount = formatted;
// 				user.error = null;
// 			}
// 			return user;
// 		});

// 		const count = userListAmounts.reduce((acc, item) => {
// 			return !item.amount || item.amount <= 0 ? acc + 1 : acc;
// 		}, 0);

// 		if (count === 0) {
// 			setError(null);
// 		}
// 		setUsersList(userListAmounts);
// 	};

// 	const filteredUsers = usersList.filter((user) => {
// 		if (filter === 'payment-entered') return user?.amount && user.amount > 0;
// 		if (filter === 'payment-not-entered')
// 			return !user.amount || user.amount <= 0;

// 		return true;
// 	});

// 	return (
// 		<div className="space-y-2 w-[28.43rem]">
// 			<Typography type="regular-16">
// 				Enter Individual Settlement Amounts
// 			</Typography>
// 			<div className="border border-opacity-primary p-6 space-y-4 rounded-2xl h-[16rem]">
// 				<div className="flex justify-between w-full">
// 					<div className="w-[56%]">
// 						<HeaderFilterTable
// 							title={'Client Name'}
// 							searchTitle="Client"
// 							filterOptions={optionsFilter}
// 							handleClearAll={() => setFilter(undefined)}
// 							handleSelectFilters={setFilter}
// 							hasFilter={!!filter}
// 							classNamePopoverContent="lg:w-[262px]"
// 						/>
// 					</div>
// 					<div className="flex-1">
// 						<Typography type="regular-12" className="text-opacity-black">
// 							Settlement Amount
// 						</Typography>
// 					</div>
// 				</div>
// 				<div className="overflow-x-auto h-[11rem] scroll-custom space-y-3">
// 					{filteredUsers.map((item, index) => (
// 						<div key={index} className="flex justify-between">
// 							<div className="flex-1">
// 								<AvatarName
// 									size={'small'}
// 									photoUrl={item.user_profile_picture || ''}
// 									firstname={`${item.user_firstname}`}
// 									lastname={`${item.user_lastname}`}
// 								/>
// 							</div>
// 							<div className="flex-1 flex items-center gap-2 justify-center relative">
// 								<Icon
// 									src={Icons.infoCircle}
// 									className={clsx(
// 										'!size-4 text-main-red mt-1 absolute left-2 top-1',
// 										{
// 											hidden: !item.error?.message,
// 										}
// 									)}
// 								/>
// 								<div className="w-[145px] flex items-center relative">
// 									<Icon
// 										src={Icons.currencyBlack}
// 										className="!size-4 absolute left-3"
// 									/>
// 									<input
// 										type="text"
// 										value={item.formattedAmount}
// 										onBlur={() => handleBlur(item.application_id)}
// 										onChange={(e) => handleChange(e, item.application_id)}
// 										className={clsx(
// 											'outline-none pl-8 pr-4 py-3 border border-opacity-primary rounded-full h-[1.875rem] w-full',
// 											{
// 												'!border-main-red': item.error?.message,
// 											}
// 										)}
// 									/>
// 								</div>
// 							</div>
// 						</div>
// 					))}
// 				</div>
// 			</div>
// 		</div>
// 	);
// };
